import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

// The only date-picker in this design system — every other date field in
// the app still uses a native <input type="date">. Built on date-fns
// (already a dependency here) rather than pulling in react-day-picker or
// similar, since a plain month-grid picker doesn't need a whole library.

export interface DatePickerProps {
  /** 'YYYY-MM-DD', or null/empty for no selection. */
  value: string | null;
  /** Receives 'YYYY-MM-DD', or '' when cleared — matches the native
   * `<input type="date">` onChange contract this replaces. */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** 'YYYY-MM-DD' inclusive bounds. */
  min?: string;
  max?: string;
  id?: string;
  className?: string;
  /** Forwarded to the trigger button — pairs with an error/hint element's
   * id for the same aria-describedby contract any other field follows. */
  ariaDescribedBy?: string;
}

function parseDay(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = parseISO(s);
  return isValid(d) ? d : null;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  disabled,
  min,
  max,
  id,
  className,
  ariaDescribedBy,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseDay(value);
  const minDate = parseDay(min);
  const maxDate = parseDay(max);
  const [visibleMonth, setVisibleMonth] = React.useState(() => selected ?? new Date());
  // Roving tabindex target — the one day cell in the grid that's Tab-reachable.
  // Tracks whichever cell last had keyboard focus so re-entering the grid with
  // Tab resumes where the agent left off, not always day 1.
  const [activeDate, setActiveDate] = React.useState<Date | null>(() => selected ?? new Date());
  const dayButtonRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  // Set by an arrow-key press that lands outside the visible month; the
  // month-change effect below focuses this date once the new `days` grid
  // (recomputed from the new `visibleMonth`) actually contains it.
  const pendingFocusDateRef = React.useRef<Date | null>(null);

  React.useEffect(() => {
    // Depend on the stable `value` string, not `selected` — parseDay(value)
    // returns a new Date object every render, so depending on `selected`
    // re-ran this on every re-render while open (any parent state change),
    // stomping Prev/Next month navigation back to the selected month.
    if (open) {
      setVisibleMonth(selected ?? new Date());
      setActiveDate(selected ?? new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  const days = React.useMemo(() => {
    const start = startOfWeek(startOfMonth(visibleMonth));
    const end = endOfWeek(endOfMonth(visibleMonth));
    return eachDayOfInterval({ start, end });
  }, [visibleMonth]);

  // Prev/Next month swap `days` without moving activeDate, so on most
  // transitions the old activeDate isn't in the new grid at all — without
  // this, every cell's isActive would read false and the grid would lose
  // its one required tab stop until the agent picks a new date.
  const activeDateInGrid = activeDate ? days.some((d) => isSameDay(d, activeDate)) : false;

  React.useEffect(() => {
    if (!pendingFocusDateRef.current) return;
    const target = pendingFocusDateRef.current;
    const idx = days.findIndex((d) => isSameDay(d, target));
    if (idx >= 0) dayButtonRefs.current[idx]?.focus();
    pendingFocusDateRef.current = null;
  }, [days]);

  const isDisabledDay = (d: Date) =>
    (minDate ? isBefore(d, minDate) : false) || (maxDate ? isAfter(d, maxDate) : false);

  const handleDayKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, d: Date) => {
    let delta: number | null = null;
    switch (e.key) {
      case 'ArrowLeft':
        delta = -1;
        break;
      case 'ArrowRight':
        delta = 1;
        break;
      case 'ArrowUp':
        delta = -7;
        break;
      case 'ArrowDown':
        delta = 7;
        break;
      case 'Home':
        delta = -d.getDay();
        break;
      case 'End':
        delta = 6 - d.getDay();
        break;
      default:
        return;
    }
    e.preventDefault();
    const target = addDays(d, delta);
    setActiveDate(target);
    if (isSameMonth(target, visibleMonth)) {
      const idx = days.findIndex((day) => isSameDay(day, target));
      dayButtonRefs.current[idx]?.focus();
    } else {
      pendingFocusDateRef.current = target;
      setVisibleMonth(startOfMonth(target));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="secondary"
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          className={cn('w-full justify-start gap-2 font-normal', !selected && 'text-text-muted', className)}
        >
          <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {selected ? format(selected, 'MMM d, yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 max-w-[90vw] p-3">
        <div className="mb-2 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Previous month"
            onClick={() => setVisibleMonth((m) => subMonths(m, 1))}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <span className="text-body font-medium text-text-primary">{format(visibleMonth, 'MMMM yyyy')}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Next month"
            onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        {/* Horizontally scrollable, not shrunk — 7 columns of 44px touch
            targets (the design system's accessibility floor, AGENTS.md
            L13) need ~332px minimum. Letting cells shrink to fit a
            320px popover dropped them to ~34px, which fixed the earlier
            overflow but violated the floor it was fixing around (Codex
            finding). Scrolling ~30-50px on the narrowest phones is a
            smaller compromise than a sub-floor touch target. */}
        <div className="overflow-x-auto">
          <div className="min-w-[332px]">
            <div className="grid grid-cols-7 gap-1 text-center text-caption text-text-muted">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <div key={i} className="py-1">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, i) => {
                const dayDisabled = isDisabledDay(d);
                const isSelected = selected ? isSameDay(d, selected) : false;
                const isActive = activeDateInGrid ? isSameDay(d, activeDate!) : i === 0;
                return (
                  <button
                    key={d.toISOString()}
                    ref={(el) => {
                      dayButtonRefs.current[i] = el;
                    }}
                    type="button"
                    // Not the native `disabled` attribute — a disabled button
                    // can't receive focus(), which would strand arrow-key nav
                    // the moment it reaches a bounded-out date. aria-disabled
                    // keeps the cell in the tab/focus flow while the onClick
                    // guard below still blocks selecting it.
                    aria-disabled={dayDisabled}
                    tabIndex={isActive ? 0 : -1}
                    aria-label={format(d, 'EEEE, MMMM d, yyyy')}
                    aria-current={isToday(d) ? 'date' : undefined}
                    aria-pressed={isSelected}
                    onKeyDown={(e) => handleDayKeyDown(e, d)}
                    onClick={() => {
                      if (dayDisabled) return;
                      onChange(format(d, 'yyyy-MM-dd'));
                      setOpen(false);
                    }}
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-md text-body tabular-nums transition-colors',
                      !isSameMonth(d, visibleMonth) && 'text-text-muted/50',
                      isSameMonth(d, visibleMonth) && !isSelected && 'text-text-primary hover:bg-bg-elevated',
                      isSelected && 'bg-accent-500 text-text-on-brand hover:bg-accent-600',
                      isToday(d) && !isSelected && 'font-semibold text-accent-500',
                      dayDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                    )}
                  >
                    {format(d, 'd')}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {value ? (
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
